/**
 * Wires usage counting to the app: where "is it switched on" and "what build is
 * this" come from.
 *
 * Kept out of `index.js` on purpose. That module is the contract and has no
 * app-specific imports, so it stays trivially testable and cannot accidentally
 * grow a dependency on a store; this file is the only place that knows about
 * preferences, Tauri or the DOM.
 */
import { getVersion } from '@tauri-apps/api/app';
import { usePreferencesStore } from '../../store/preferencesStore';
import { initTelemetry, flushTelemetryNow, resetTelemetry } from './index';
import { isSyntheticRun } from '../../utils/syntheticRun';
import { startMcpUsageDrain } from './mcpUsage';

/** Resolved once at startup; until then counters report "unknown", which the server accepts. */
let appVersion = 'unknown';

/** Coarse OS bucket — the same three the release manifest uses. Never a full user-agent. */
function osBucket() {
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  if (/Windows/i.test(ua)) return 'windows';
  if (/Mac OS X|Macintosh/i.test(ua)) return 'macos';
  if (/Linux|X11/i.test(ua)) return 'linux';
  return 'unknown';
}

/**
 * May feature usage leave the machine? Only after the user said yes (owner,
 * 16.10.2026): the preferences are loaded, the question was answered, and the
 * answer was "Share". Before hydration the store holds the defaults (off), so
 * nothing is sent while the stored answer is still being read.
 */
export function usageSharingAllowed(state = usePreferencesStore.getState()) {
  return Boolean(state && state.hydrated === true && state.usageDataAsked === true && state.sendUsageData === true);
}

/**
 * Start usage counting. Returns a cleanup function, so it can be used directly
 * as a `useEffect` body.
 */
export function startUsageCounting() {
  initTelemetry({
    // A recorded demo or e2e run is not a user; counting it would put our own
    // test traffic into the feature numbers the dashboard is for.
    isEnabled: () => !isSyntheticRun() && usageSharingAllowed(),
    getEnv: () => ({ appVersion, os: osBucket() }),
  });

  getVersion().then((v) => { if (v) appVersion = v; }).catch(() => {});

  // Switching the toggle OFF drops anything queued and forgets today's marks,
  // so switching it back on starts counting again rather than staying silent
  // until tomorrow.
  let previous = usageSharingAllowed();
  const unsubscribe = usePreferencesStore.subscribe((state) => {
    const now = usageSharingAllowed(state);
    if (now !== previous) { previous = now; resetTelemetry(); }
  });

  // Best-effort flush of a batch still waiting when the window goes away. NOT
  // complete coverage: closing to tray is a native window hide, which does not
  // reliably fire `pagehide`, and `visibilitychange` on a hidden-but-alive
  // webview is inconsistent across platforms. Up to one batch can be lost that
  // way — acceptable because the daily mark is released on a failed send and
  // each key only needs to arrive once a day, from any session.
  const onHide = () => { if (document.visibilityState === 'hidden') flushTelemetryNow(); };
  document.addEventListener('visibilitychange', onHide);
  window.addEventListener('pagehide', flushTelemetryNow);

  // Which MCP client / surface was used — noted in Rust, drained here.
  const stopMcpDrain = startMcpUsageDrain();

  return () => {
    stopMcpDrain();
    unsubscribe();
    document.removeEventListener('visibilitychange', onHide);
    window.removeEventListener('pagehide', flushTelemetryNow);
  };
}
